import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { Clip } from 'components/app/editor/_mediator/editor.slice';
import { handleClips } from 'components/app/editor/_mediator/editor.slice';
import { clipsApi } from 'components/app/editor/_mediator/editor-clips/editor-clips.helper';
import { sbApi, handleSbLinks } from 'components/app/editor/_mediator/editor-clips/editor-clips-sb.helper';
import { setStoryboard } from 'services/storyboard.service';
import { setClips } from 'services/clip.service';
import { Error } from 'components/app/_helpers/app-errors.helper';
import { selectThumbnailData } from 'components/app/_mediator/clip-thumb/clip-thumb.slice';
import { toast } from 'react-toastify';

/**
 * Hook for pinning/unpinning highlights to/from the storyboard
 */
export const useHighlightPin = (clipData: Clip) => {
  const dispatch = useAppDispatch();
  const selectedStory = useAppSelector((state) => state.projects.selectedStory);
  const storyBoard = useAppSelector((state) => state.editor.storyBoard);
  const clipsMap = useAppSelector((state) => state.editor.clipsMap);
  const selector = useAppSelector((state) => state.editor.selector);
  const linkedClips = useAppSelector((state) => state.editor.linkedClips);
  const sbPosition = useAppSelector((state) => state.editor__controls.sbPosition);
  const clipThumbnails = useAppSelector((state) => state.clipThumb.clipThumbnails);

  const togglePin = async () => {
    if (!selectedStory || !clipData.mediaFileId || clipData.startTime == null) {
      Error.addClip.invalidStartTime();
      return;
    }

    if (clipData.isPinned ?? false) {
      // Unpin: Remove clip from storyboard
      await unpinHighlight();
    } else {
      // Pin: Create clip and add to storyboard
      await pinHighlight();
    }
  };

  const pinHighlight = async () => {
    if (!clipsMap || !selector || !storyBoard || !clipData.highlightId) {
      return;
    }

    // Create a new clip from the highlight data with isPinned = true
    const clip: Clip = {
      ...clipData,
      clipGroupId: clipData.clipGroupId || 0,
      isPinned: true,
    };

    // When pinning a highlight, it's already in the selector, so we don't want to add it again
    // Use clipsApi().add() but then manually fix the selector to avoid duplicates
    const position = sbApi(storyBoard).validateAddClipPosition(sbPosition, 'startTime');
    const res = clipsApi(selector, storyBoard, linkedClips).add([clip], position);

    // Check if clip is already in selector to avoid duplicates
    const fileClips = selector[clip.mediaFileId] || [];
    const isAlreadyInSelector = fileClips.includes(clip.clipId);

    // If already in selector, don't add it again (keep original selector)
    // Otherwise, use the updated selector from clipsApi
    const finalSelector = isAlreadyInSelector ? selector : res.selector;

    const resWithFixedSelector = {
      ...res,
      selector: finalSelector,
    };

    const res1 = handleSbLinks(res.linkedClips, res.storyBoard, storyBoard);
    if (res1.status === 'cantBeFixed') {
      Error.handleSbLinks();
      return;
    }

    try {
      // Get thumbnail URL from clip thumbnails state
      const thumbnailData = selectThumbnailData(clip.clipId, clipThumbnails);
      const thumbnailUrl = thumbnailData?.url || null;

      // Prepare clip payload with highlightId - ONLY the single clip being pinned
      const clipPayload: Record<
        string,
        {
          clipGroupId: number;
          config: Clip['config'];
          thumbnailUrl: string | null;
          score?: number;
          highlightId?: number;
        }
      > = {
        [clip.clipId]: {
          clipGroupId: clip.clipGroupId,
          config: clip.config,
          thumbnailUrl: thumbnailUrl,
          score: clip.score,
          highlightId: clip.highlightId,
        },
      };

      // Verify we're only sending one clip
      const clipIds = Object.keys(clipPayload);
      if (clipIds.length !== 1 || clipIds[0] !== clip.clipId) {
        toast.error('Error: Invalid clip payload');
        return;
      }

      // Update clips via API - only the single pinned clip
      // NOTE: Frontend correctly sends only ONE clip, but backend may be creating
      // clips for all highlights in the file. This needs to be investigated on the backend.
      await setClips({
        storyId: selectedStory!,
        data: clipPayload,
      });

      // Update storyboard
      await setStoryboard({
        storyId: selectedStory!,
        ...res1.storyBoard,
      });

      // Update local state
      // The clip already has isPinned = true from above, and clipsApi.add() will add it to clipsMap
      dispatch(
        handleClips({
          data: { ...resWithFixedSelector, storyBoard: res1.storyBoard },
          backupParams: { title: 'Add Clip', action: 'addClip', activeClip: [null, clip.clipId] },
        })
      );
    } catch (error) {
      toast.error('Failed to pin highlight to storyboard');
    }
  };

  const unpinHighlight = async () => {
    if (!clipsMap || !storyBoard || !clipData.clipId || !selector) return;

    // Find the clip in the storyboard
    const clip = clipsMap[clipData.clipId];
    if (!clip) return;

    // Remove clip from storyboard
    const res = clipsApi(selector, storyBoard, linkedClips).del([clip]);
    const res1 = handleSbLinks(res.linkedClips, res.storyBoard, storyBoard);

    try {
      // Update storyboard via API
      await setStoryboard({
        storyId: selectedStory!,
        ...res1.storyBoard,
      });

      // Update local state
      // When unpinning, the clip is removed from storyboard but stays in clipsMap and selector
      // We need to update the clip in clipsMap to set isPinned = false
      // Don't use res.clipsMap (which sets clip to null), instead preserve the clip with updated isPinned
      // Don't use res.selector (which removes clip from selector), preserve original selector so highlight stays visible
      const updatedClipsMap = {
        ...res.clipsMap,
        [clipData.clipId]: { ...clip, isPinned: false },
      };

      dispatch(
        handleClips({
          data: { ...res, clipsMap: updatedClipsMap, selector: selector, storyBoard: res1.storyBoard },
          backupParams: { title: 'Delete Clip', action: 'delClip', activeClip: [clipData.clipId, null] },
        })
      );
    } catch (error) {
      console.error('Failed to unpin highlight:', error);
      toast.error('Failed to unpin highlight from storyboard');
    }
  };

  return { togglePin };
};
